/**
 * `<Link>` / `<a>` に 005 `Button` 相当の外観クラス文字列を与えるユーティリティ（Unit 008）。
 *
 * 目的: ナビゲーションは `<a>` 遷移（href）を維持しつつ、見た目だけ 005 `Button` に揃える。
 * 005 `Button` は `<button>` 専用（polymorphic 非対応）のため、見た目クラスのみを切り出す。
 *
 * 設計方針:
 * - `components/ui/cn` には依存せず utils 内で完結（`components/ui/` への逆依存を作らない）。
 * - クラス文字列は `Button.tsx` の BASE + VARIANT + SIZE.md に視覚一致させる（差異が出た場合の収斂はバックログ）。
 * - 色 / padding / radius は variant が確定（`extra` は `w-full` / `block` 等のレイアウト系のみ末尾追記する想定）。
 * - `<a>` は disabled 状態を持たないため、Button BASE の `disabled:*` クラスは含めない。
 */

export type LinkButtonVariant = 'primary' | 'secondary'
export type LinkButtonSize = 'sm' | 'md'

// Button.tsx の BASE（disabled:* を除く）と一致
const BASE =
  'inline-flex items-center justify-center gap-2 font-medium rounded-control ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-primary focus-visible:ring-offset-2'

// Button.tsx の VARIANT.primary / VARIANT.secondary と一致
const VARIANT: Record<LinkButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover',
  secondary:
    'bg-white text-primary border-[1.5px] border-primary hover:bg-primary hover:text-white',
}

// Button.tsx の SIZE.sm / SIZE.md と一致（Unit 004: py-2.5=10px でコンパクト化）
const SIZE: Record<LinkButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-8 py-2.5 text-base',
}

/**
 * Button 相当の外観クラスを返す。
 * @param variant 'primary'（既定） | 'secondary'
 * @param extra レイアウト系クラス（'w-full' / 'block' 等）を末尾に追記
 * @param size 'md'（既定） | 'sm'。SP でのコンパクト化などに使用
 */
export function linkButtonClass(
  variant: LinkButtonVariant = 'primary',
  extra = '',
  size: LinkButtonSize = 'md',
): string {
  return [BASE, VARIANT[variant], SIZE[size], extra].filter(Boolean).join(' ')
}
